import { type ReactNode, useId, useMemo, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';

import {
    MenuContext,
    type MenuContextValue,
    type MenuDirection,
    type MenuKind,
    type PendingFocus,
} from './MenuContext';

import styles from './Menu.module.scss';

export interface MenuProps {
    children: ReactNode;
    /**
     * `menu` holds actions and choices and gets full keyboard support. `panel` holds a form
     * of its own (date fields, a search, buttons) and leaves its keys to the browser. `list`
     * is a panel of controls, such as checkboxes, that the arrow keys open onto. Panels and
     * lists close on Escape and hand focus back to the trigger.
     */
    kind?: MenuKind;
    /** Controlled open state. Leave it out and the menu keeps its own. */
    isOpen?: boolean;
    /**
     * Called with the state the trigger, an item, Escape or a click outside asks for. It
     * carries the state rather than a flip, so a handler that also closes the menu agrees
     * with the item that closes it instead of opening it again.
     */
    onOpenChange?: (open: boolean) => void;
    /** Which way the menu opens from its trigger. */
    direction?: MenuDirection;
    /** Stops the menu opening. One already open can still close. */
    disabled?: boolean;
    /** Applied to the box around the trigger and the popup. */
    className?: string;
}

/**
 * The portal's dropdown: one trigger, one popup, positioned against the trigger and closed
 * by Escape or a click outside. A `menu` is Radix's DropdownMenu, with its item roles and
 * roving focus; a `panel` or `list` is Radix's Popover, which leaves the keys of the fields
 * and buttons inside it alone. Neither is modal: the page keeps scrolling and a click
 * outside still lands where it was aimed.
 */
export function Menu(props: MenuProps) {
    const {
        children,
        kind = 'menu',
        isOpen: controlledOpen,
        onOpenChange,
        direction = 'down',
        disabled = false,
        className,
    } = props;

    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = controlledOpen ?? uncontrolledOpen;
    const triggerRef = useRef<HTMLElement | null>(null);
    const contentRef = useRef<HTMLElement | null>(null);
    const pendingFocus = useRef<PendingFocus>(null);
    // React ids hold colons, which jsdom's selector engine chokes on in tests.
    const baseId = useId().replace(/:/g, '');

    // Every request is measured against the state on screen, never against an earlier
    // request in the same event: two closes from one click both ask for closed.
    const setOpen = (open: boolean) => {
        if (open === isOpen || (open && disabled)) return;
        if (!open) pendingFocus.current = null;
        if (controlledOpen === undefined) {
            setUncontrolledOpen(open);
        }
        onOpenChange?.(open);
    };

    const setOpenRef = useRef(setOpen);
    setOpenRef.current = setOpen;

    const context = useMemo<MenuContextValue>(
        () => ({
            kind,
            isOpen,
            direction,
            setOpen: (open: boolean) => setOpenRef.current(open),
            triggerRef,
            triggerId: `menu-trigger-${baseId}`,
            contentId: `menu-${baseId}`,
            contentRef,
            pendingFocus,
        }),
        [kind, isOpen, direction, baseId]
    );

    const root =
        kind === 'menu' ? (
            <DropdownMenu.Root open={isOpen} onOpenChange={context.setOpen} modal={false}>
                {children}
            </DropdownMenu.Root>
        ) : (
            <Popover.Root open={isOpen} onOpenChange={context.setOpen}>
                {children}
            </Popover.Root>
        );

    return (
        <MenuContext value={context}>
            <div className={classNames(styles.root, className)}>{root}</div>
        </MenuContext>
    );
}

export default Menu;
